import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { loadVhuBids, loadVhuLots } from "./vhuLotService";

const bidStatusColor = {
  GAGNANTE: "success",
  EN_COURS: "primary",
  DEPASSEE: "secondary",
  ANNULEE: "danger",
};

const sortOptions = [
  { value: "date_desc", label: "Plus recentes" },
  { value: "date_asc", label: "Plus anciennes" },
  { value: "amount_desc", label: "Montant decroissant" },
  { value: "amount_asc", label: "Montant croissant" },
  { value: "lot_asc", label: "Enchere A-Z" },
  { value: "vehicle_asc", label: "Vehicule A-Z" },
];

const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const boolLabel = (value) => (value ? "Oui" : "Non");
const lotLabel = (lot) => lot?.reference || lot?.titre || (lot?.id ? `Lot #${lot.id}` : "Enchere");
const bidLotLabel = (bid) => bid.lotReference || bid.lotTitre || `Lot #${bid.lotId}`;
const bidVehicleLabel = (bid) => bid.vehiculeLabel || (bid.vehiculeId ? `Vehicule #${bid.vehiculeId}` : "Vehicule non renseigne");
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

function VhuBidOffersPage() {
  const [lots, setLots] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lotFilter, setLotFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [lotResponse, bidResponse] = await Promise.all([
        loadVhuLots(),
        loadVhuBids(),
      ]);
      setLots(lotResponse?.data?.body || []);
      setBids(bidResponse?.data?.body || []);
    } finally {
      setLoading(false);
    }
  };

  const visibleBids = useMemo(() => {
    const search = query.trim().toLowerCase();
    const filtered = bids.filter((bid) => {
      const matchesLot = !lotFilter || String(bid.lotId) === String(lotFilter);
      const matchesVehicle = !vehicleFilter || String(bid.vehiculeId) === String(vehicleFilter);
      const matchesStatus = !statusFilter || bid.status === statusFilter;
      const matchesQuery = !search || [
        bid.ferrailleurNom,
        bid.lotReference,
        bid.lotTitre,
        bid.vehiculeLabel,
        bid.commentaire,
      ].filter(Boolean).join(" ").toLowerCase().includes(search);
      return matchesLot && matchesVehicle && matchesStatus && matchesQuery;
    });

    return [...filtered].sort((left, right) => {
      if (sortBy === "amount_desc") return Number(right.montant || 0) - Number(left.montant || 0);
      if (sortBy === "amount_asc") return Number(left.montant || 0) - Number(right.montant || 0);
      if (sortBy === "date_asc") return new Date(left.dateOffre || 0) - new Date(right.dateOffre || 0);
      if (sortBy === "lot_asc") return bidLotLabel(left).localeCompare(bidLotLabel(right), "fr", { sensitivity: "base" });
      if (sortBy === "vehicle_asc") return bidVehicleLabel(left).localeCompare(bidVehicleLabel(right), "fr", { sensitivity: "base" });
      return new Date(right.dateOffre || 0) - new Date(left.dateOffre || 0);
    });
  }, [bids, lotFilter, vehicleFilter, statusFilter, query, sortBy]);

  const vehicleOptions = useMemo(() => {
    const byId = new Map();
    lots.forEach((lot) => {
      if (lotFilter && String(lot.id) !== String(lotFilter)) return;
      (lot.vehicules || []).forEach((vehicle) => {
        if (!vehicle?.id) return;
        byId.set(String(vehicle.id), [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`);
      });
    });
    bids.forEach((bid) => {
      if (lotFilter && String(bid.lotId) !== String(lotFilter)) return;
      if (bid.vehiculeId && !byId.has(String(bid.vehiculeId))) {
        byId.set(String(bid.vehiculeId), bidVehicleLabel(bid));
      }
    });
    return Array.from(byId, ([id, label]) => ({ id, label })).sort((left, right) => left.label.localeCompare(right.label, "fr", { sensitivity: "base" }));
  }, [lots, bids, lotFilter]);

  const summary = useMemo(() => {
    const bestAmount = visibleBids.reduce((max, bid) => Math.max(max, Number(bid.montant || 0)), 0);
    const vehicleCount = new Set(visibleBids.map((bid) => bid.vehiculeId).filter(Boolean).map(String)).size;
    const lotCount = new Set(visibleBids.map((bid) => bid.lotId).filter(Boolean).map(String)).size;
    const winningCount = visibleBids.filter((bid) => bid.isBestBid || bid.status === "GAGNANTE").length;
    return { bestAmount, lotCount, vehicleCount, winningCount };
  }, [visibleBids]);

  const resetFilters = () => {
    setLotFilter("");
    setVehicleFilter("");
    setStatusFilter("");
    setQuery("");
    setSortBy("date_desc");
  };

  return (
    <div className="page-content auction-page vhu-lots-page vhu-offers-page">
      <Container fluid>
        <Breadcrumbs title="Offres VHU" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Offres VHU", link: "#" }]} />

        <Row className="g-3 mb-3">
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat success">
              <span>Offres affichées</span>
              <strong>{visibleBids.length}</strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat info">
              <span>Enchères</span>
              <strong>{summary.lotCount}</strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat warning">
              <span>Véhicules</span>
              <strong>{summary.vehicleCount}</strong>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="vhu-offer-stat dark">
              <span>Meilleure offre</span>
              <strong>{moneyLabel(summary.bestAmount)}</strong>
            </div>
          </Col>
        </Row>

        <Card className="mb-4 vhu-list-toolbar vhu-offer-filters">
          <CardBody>
            <div className="vhu-filter-head">
              <div>
                <span>Analyse des offres</span>
                <h5>Filtrer par enchere et vehicule</h5>
              </div>
              <button type="button" className="btn btn-light btn-sm" onClick={resetFilters}>
                <i className="ri-refresh-line me-1" />
                Réinitialiser
              </button>
            </div>
            <Row className="g-3 align-items-end">
              <Col xl={3} md={6}>
                <Label>Enchere</Label>
                <Input type="select" value={lotFilter} onChange={(event) => {
                  setLotFilter(event.target.value);
                  setVehicleFilter("");
                }}>
                  <option value="">Toutes les encheres</option>
                  {lots.map((lot) => (
                    <option key={lot.id} value={lot.id}>{lotLabel(lot)}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={3} md={6}>
                <Label>Vehicule</Label>
                <Input type="select" value={vehicleFilter} onChange={(event) => setVehicleFilter(event.target.value)}>
                  <option value="">Tous les vehicules</option>
                  {vehicleOptions.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.label}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>Statut</Label>
                <Input type="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="">Tous les statuts</option>
                  <option value="GAGNANTE">Gagnante</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="DEPASSEE">Depassee</option>
                  <option value="ANNULEE">Annulee</option>
                </Input>
              </Col>
              <Col xl={2} md={6}>
                <Label>Tri</Label>
                <Input type="select" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </Col>
              <Col xl={2} md={12}>
                <Label>Recherche</Label>
                <div className="vhu-search-control mb-0">
                  <i className="ri-search-line" />
                  <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Acheteur, lot..." />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : !visibleBids.length ? (
          <div className="vhu-empty-state">
            <i className="ri-auction-line" />
            <strong>Aucune offre a afficher</strong>
            <span>Les offres envoyees depuis le mobile apparaitront ici.</span>
          </div>
        ) : (
          <Card className="vhu-bids-panel">
            <CardBody>
              <div className="vhu-bid-list">
                {visibleBids.map((bid) => (
                  <div className={bid.isBestBid ? "vhu-bid-row best" : "vhu-bid-row"} key={bid.id}>
                    <div className="vhu-bid-accent">
                      <i className={bid.isBestBid ? "ri-trophy-line" : "ri-auction-line"} />
                    </div>
                    <div className="vhu-bid-main">
                      <div className="vhu-bid-heading">
                        <div>
                          <strong>{bid.ferrailleurNom || "Acheteur"}</strong>
                          <span>{bidLotLabel(bid)}</span>
                          <small>{bidVehicleLabel(bid)}</small>
                        </div>
                        <div className="vhu-bid-actions">
                          {(bid.isBestBid || bid.status === "GAGNANTE") && <Badge color="success">Meilleure offre vehicule</Badge>}
                          <Badge color={bidStatusColor[bid.status] || "secondary"}>{bid.status || "-"}</Badge>
                        </div>
                      </div>
                      <div className="vhu-bid-details">
                        <span><b>Montant</b>{moneyLabel(bid.montant)}</span>
                        <span><b>Vehicule</b>{bid.vehiculeLabel || (bid.vehiculeId ? `#${bid.vehiculeId}` : "-")}</span>
                        <span><b>Date offre</b>{dateLabel(bid.dateOffre)}</span>
                        <span><b>Delai paiement</b>{bid.delaiPaiement || "-"}</span>
                        <span><b>Delai enlevement</b>{bid.delaiEnlevement || "-"}</span>
                        <span><b>Moyen paiement</b>{bid.moyenPaiementPrevu || "-"}</span>
                        <span><b>Conditions</b>{boolLabel(bid.termsAccepted)}</span>
                        <span><b>Offre ferme</b>{boolLabel(bid.firmOfferAccepted)}</span>
                      </div>
                      {bid.commentaire && (
                        <div className="vhu-bid-comment">
                          <b>Commentaire</b>
                          <span>{bid.commentaire}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
}

export default VhuBidOffersPage;
